import React from 'react';
import styled from 'styled-components';
import {Field, ErrorMessage} from 'formik';

import {SelectionControl, SelectionControlGroup} from '../SelectionControl';
import {FormLabel} from '../../../form-labels/FormLabel';
import {ErrorMessageText, FormErrorMessage} from '../../../../../ui-toolkit/form-v2/form-labels/FormErrorMessage';

/**
 * This component allows you to create multiple checkboxes. It is designed for use cases such as multi-select options.
 *
 * @param {object} param0
 *
 * @param {string} [param0.name]
 * Ensure to include the `name` property when using a Formik-type form control. Without it, the form control may not function properly.
 * Additionally, remember to wrap `<Formik></Formik>` around this and all other form control types to ensure proper functionality.
 * Adding the name to a Formik Input component is essential because Formik uses the name prop to link the value of the  input to the Formik state.
 * Note that the Formik type is the default version injected into the form control you are applying.
 *
 * @param {React.ReactNode} [param0.label]
 * The text to be displayed as the label.
 *
 * @param {{label: string, value: (string|number|boolean)}[]} [param0.options]
 * The array of options for the checkboxes.
 *
 * @param {string} [param0.value]
 * The value associated with the checkbox option.
 *
 * @example
 * ```jsx
 *      // == --- How to use this component:  --- ==
 *      <MultiCheckbox
 *          label={t('userEditForm.applicationRoleLabel')}
 *          name="applicationRoleIds"
 *          options={roles.map((role) => {
 *              return {
 *                  key: role.applicationRoleId,
 *                  label: role.roleName,
 *                  value: role.applicationRoleId,
 *              };
 *          })}
 *      />
 * ```
 */
export const MultiCheckbox = ({name, label, options}) => (
    <Wrapper>
        <FormLabel>{label}</FormLabel>
        <Field id={name} name={name}>
            {({field}) => {
                return options.map((option) => {
                    return (
                        <SelectionControl
                            key={option.value}
                            checked={field.value.includes(option.value.toString())}
                            checkbox
                            label={option.label}
                            htmlFor={option.value}>
                            <input
                                type="checkbox"
                                id={option.value}
                                {...field}
                                value={option.value.toString()}
                                checked={field.value.includes(option.value.toString())}
                            />
                        </SelectionControl>
                    );
                });
            }}
        </Field>
        <ErrorMessage name={name} component={FormErrorMessage} />
    </Wrapper>
);

const Wrapper = styled.div`
    margin-bottom: var(--space-md);
    ${SelectionControlGroup} {
        margin-bottom: var(--space-xsm);
    }
    ${SelectionControlGroup} > label {
        width: max-content;
    }

    &:has(${ErrorMessageText.toString()}) {
        *,
        ${SelectionControlGroup} span {
            color: var(--danger);
        }
    }
`;
